import { SECOND_MS } from '@mail-core/std';
import { xray } from 'lib/xray';
import type { GroupClearedInfo } from 'reactApp/api/quotaCleaner/QuotaCleanerAPICall.types';
import { fastCleanGroupsUpdate } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.actions';
import { FastCleanSelectors } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import type { QuotaCleaneFastGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';
import { store as reduxStore } from 'reactApp/store';
import { put, select } from 'redux-saga/effects';

const QUEUE_SAFE_SEC_TIMEOUT = 10;

type FastCleanFailResultType = 'no_clean' | 'remove_only' | 'remove_and_safe' | 'unexpected';
type FastCleanSuccessResultType = 'remove_only' | 'remove_and_safe';
export type FastCleanResultType = FastCleanFailResultType | FastCleanSuccessResultType;

export type FastCleanProcessReturnValueType = {
    status: boolean;
    result: FastCleanResultType;
    data: { totalCleared: number; overquotaBytes: number; expectedTotalCleared: number };
};

/**
 * Принимает очередь событий об частичной очистке группы чистилки
 * и отправляет их в соответствии с timeout
 * @param queue – очередь событий
 * @param timeout - период обработки
 */
export const executeActionsQueue = (queue: ReturnType<typeof fastCleanGroupsUpdate>[], timeout: number) => {
    let i = 0;
    const myInterval = setInterval(() => {
        if (i >= queue.length) {
            clearInterval(myInterval);
            return;
        }

        reduxStore.dispatch(queue[i]);
        i++;
    }, timeout);
};

/**
 * Для групп, очищенных синхронно, – просто уведомляет стор о завершении очистки
 * Для группы, очищенной асинхронно строит очередь из событий,
 * равномерно уведомляющих стор о стадиях очистки этой группы, и передает её в executeActionsFunc
 * Также считает время, через которое завершится последняя асинхронная очистка
 * @param groups – набор групп
 * @param executeActionsFunc - функция обработки очереди событий очистки
 * @returns количество секунд, через которое завершится очистка переданных групп
 */
export function* saveFastCleanGroupsSuccess(groups: GroupClearedInfo[], executeActionsFunc = executeActionsQueue) {
    let maxSecondsLeft = 0;

    for (const group of groups) {
        const groupId = group.group_id;

        let secondsLeft = Math.ceil(
            group.result?.mail_moved_to_safe?.seconds_left ||
                group.result?.mail_deleted?.seconds_left ||
                group.result?.cloud_moved_to_safe?.seconds_left ||
                group.result?.cloud_deleted?.seconds_left ||
                0
        );

        // если очистка не асинхронная, сразу апдейтим стейт
        if (!secondsLeft) {
            yield put(
                fastCleanGroupsUpdate([
                    {
                        groupId,
                        cleared: group.result.total_cleared,
                    },
                ])
            );
            continue;
        }

        secondsLeft += QUEUE_SAFE_SEC_TIMEOUT;

        maxSecondsLeft = Math.max(maxSecondsLeft, secondsLeft);

        xray.send('quota-fast-cln-seconds-left', { dwh_enabled: true, dwh: { value: secondsLeft } });

        // если асинхронная, апдейтим равномерно в течение оставшегося времени (фейковая загрузка)

        const groupInfo: QuotaCleaneFastGroup | undefined = yield select(FastCleanSelectors.getGroup, groupId);
        const groupSizeExpected = groupInfo?.size_uniq || 0;
        const groupSizeCleared = group.result?.total_cleared?.size;
        const groupSize = groupSizeCleared < groupSizeExpected ? groupSizeCleared : groupSizeExpected;

        const sizeStep = Math.ceil((groupSize || 0) / secondsLeft);
        const countStep = Math.ceil((group.result?.total_cleared?.count || 0) / secondsLeft);
        const stepAction = fastCleanGroupsUpdate([
            {
                groupId,
                cleared: {
                    size: sizeStep,
                    count: countStep,
                },
            },
        ]);
        const lastAction = fastCleanGroupsUpdate([
            {
                groupId,
                cleared: {
                    size: sizeStep,
                    count: countStep,
                },
                isSuccess: true,
            },
        ]);

        const actionsQueue = new Array(secondsLeft - 1).fill(stepAction).concat([lastAction]);

        executeActionsFunc(actionsQueue, SECOND_MS);
    }

    return maxSecondsLeft;
}
